// selectors/social.js


// Get a draft bucket for a brandSettingsId (or "new") – whole map
export const selectDraftsForBrandSettings = (brandSettingsId) => (state) => {
  const key = String(brandSettingsId ?? 'new');
  return (state.social && state.social.drafts && state.social.drafts[key]) || {};
};

// Get a draft connection for a single platform
export const selectDraftConnectionForBS = (brandSettingsId, platform) => (state) => {
  const key = String(brandSettingsId ?? 'new');
  const plat = String(platform || 'unknown').toLowerCase();
  const drafts = (state.social && state.social.drafts && state.social.drafts[key]) || {};
  return drafts[plat] || null;
};

// Local helper to compute a sane status if the account object is present
function deriveStatusFromAccount(acc) {
  if (!acc) return 'disconnected';
  if (acc.status) return String(acc.status);
  if (acc.expiresAt) {
    return (new Date(acc.expiresAt) > new Date()) ? 'connected' : 'expired';
  }
  const hasTokens = !!acc.accessToken || !!acc.refreshToken;
  const hasPages  = Array.isArray(acc.pages) && acc.pages.length > 0;
  return (hasTokens || hasPages) ? 'connected' : 'disconnected';
}



// Optional: direct access to a draft bucket (handy in components/debug)
export const selectDraftConnectionForBrandSettings = (brandSettingsId, platform) => (state) => {
  if (!brandSettingsId || !platform) return null;
  const p = String(platform).toLowerCase();
  const id = String(brandSettingsId);
  return state?.social?.drafts?.[id]?.[p] || null;
};





export const selectSocialState = (s) => s.social;

export const selectConnection = (platform) => (s) => {
  const key = (platform || 'unknown').toLowerCase();
  return (s.social.connections && s.social.connections[key]) || null;
};

export const selectAllConnections = (s) => s.social.connections || {};

export const selectIsPlatformConnected = (platform) => (s) => {
  const c = selectConnection(platform)(s);
  return c?.status === 'connected';
};

export const selectTargetsFor = (platform) => (s) => {
  const c = selectConnection(platform)(s);
  return c?.targets || [];
};

export const selectSelectedTargetIdFor = (platform) => (s) => {
  const c = selectConnection(platform)(s);
  return c?.selectedTargetId || null;
};

export const selectMessage = (s) => s.social.message;
export const selectLoading = (s) => s.social.loading;
export const selectError = (s) => s.social.error;



// Helper to safely get nested paths
const get = (obj, path, def) =>
  path.split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj) ?? def;

/**
 * Select a SocialAccount by id from several possible locations.
 * Supports shapes like:
 *  - state.social.accounts.byId[accountId]
 *  - state.user.socialAccounts.byId[accountId]
 *  - state.user.accounts.byId[accountId]
 *  - state.social.accounts.list (array)
 */
export const selectSocialAccountById = (accountId) => (state) => {
  const id = String(accountId);
  return (
    get(state, `social.accounts.byId.${id}`) ||
    get(state, `user.socialAccounts.byId.${id}`) ||
    get(state, `user.accounts.byId.${id}`) ||
    (Array.isArray(get(state, 'social.accounts.list'))
      ? state.social.accounts.list.find((a) => String(a._id || a.id) === id)
      : undefined)
  );
};

/**
 * Select all targets for an account, with optional computed targetStatus.
 * Supports shapes like:
 *  - state.social.targets.byAccountId[accountId] = [targets]
 *  - state.user.socialTargets.byAccountId[accountId] = [targets]
 *  - state.user.socialTargets.byId (object) filtered by .account
 */
export const selectTargetsWithStatusByAccount = (accountId) => (state) => {
  const id = String(accountId);

  // Common buckets
  const listA = get(state, `social.targets.byAccountId.${id}`);
  if (Array.isArray(listA)) return listA;

  const listB = get(state, `user.socialTargets.byAccountId.${id}`);
  if (Array.isArray(listB)) return listB;

  const byId = get(state, 'user.socialTargets.byId') || get(state, 'social.targets.byId');
  if (byId && typeof byId === 'object') {
    return Object.values(byId).filter((t) => String(t.account) === id || String(t.accountId) === id);
  }

  // Fallback: none
  return [];
};

/**
 * Select the default target for an account.
 * Tries:
 *  - account.defaultTarget
 *  - first target with isDefault === true
 *  - by composed id match in state if stored as `${platform}:${externalId}`
 */
export const selectDefaultTargetForAccount = (accountId) => (state) => {
  const id = String(accountId);

  // 1) If account holds defaultTarget object
  const acc =
    get(state, `social.accounts.byId.${id}`) ||
    get(state, `user.socialAccounts.byId.${id}`) ||
    get(state, `user.accounts.byId.${id}`);
  if (acc && acc.defaultTarget) return acc.defaultTarget;

  // 2) Look in targets for an explicit default
  const targets =
    get(state, `social.targets.byAccountId.${id}`) ||
    get(state, `user.socialTargets.byAccountId.${id}`) ||
    [];
  const explicit = targets.find((t) => t.isDefault || t.default === true);
  if (explicit) return explicit;

  // 3) If you keep a string like `${platform}:${externalId}` somewhere:
  const composed = get(state, `social.defaults.byAccountId.${id}`) || get(state, `user.socialDefaults.byAccountId.${id}`);
  if (composed && typeof composed === 'string') {
    const externalId = composed.split(':').slice(-1)[0];
    const found = targets.find((t) => String(t.externalId || t.id) === externalId);
    if (found) return found;
  }

  return undefined;
};

/**
 * Brand/BrandSettings selectors — try multiple likely locations/shapes.
 */
export const selectBrandById = (brandId) => (state) => {
  if (!brandId) return null;
  const id = String(brandId);

  // common normalized shapes
  const b1 = get(state, `brands.byId.${id}`);
  if (b1) return b1;

  // sometimes nested under user
  const b2 = get(state, `user.brands.byId.${id}`);
  if (b2) return b2;

  // list fallbacks
  const listA = get(state, 'brands.list');
  if (Array.isArray(listA)) return listA.find(b => String(b._id || b.id) === id) || null;

  const listB = get(state, 'user.brands');
  if (Array.isArray(listB)) return listB.find(b => String(b._id || b.id) === id) || null;

  return null;
};

export const selectBrandSettingsById = (brandSettingsId) => (state) => {
  if (!brandSettingsId) return null;
  const id = String(brandSettingsId);

  // normalized
  const bs1 = get(state, `brandSettings.byId.${id}`);
  if (bs1) return bs1;

  // sometimes nested under user
  const bs2 = get(state, `user.brandSettings.byId.${id}`);
  if (bs2) return bs2;

  // list fallbacks
  const listA = get(state, 'brandSettings.list');
  if (Array.isArray(listA)) return listA.find(x => String(x._id || x.id) === id) || null;

  const listB = get(state, 'user.brandSettings');
  if (Array.isArray(listB)) return listB.find(x => String(x._id || x.id) === id) || null;

  return null;
};

/**
 * For a given BrandSettings + platform, return the current connection "selection"
 * (status, selected account/target/ads, and a best-effort live status).
 *
 * Expects state.brandSettings.byId[bsId].selectedByPlatform[platform] like:
 *   { accountId, targetId, adsId, externalTargetId }
 * and will also look up the SocialAccount to refine `status`.
 */
export const selectPlatformConnectionForBrandSettings = (brandSettingsId, platform) => (state) => {
  if (!brandSettingsId || !platform) return null;

  const p   = String(platform).toLowerCase();
  const bsId = String(brandSettingsId);

  // 1) DRAFTS (temporary, unconfirmed)
  const draft = state?.social?.drafts?.[bsId]?.[p];
  if (draft) {
    // Try to refine status against the accounts already inside this BrandSettings (if any)
    const bs = selectBrandSettingsById(bsId)(state) || null;
    const acc = (bs?.socialAccounts || []).find(a => String(a?._id) === String(draft.accountId)) || null;

    const status = draft.status || deriveStatusFromAccount(acc) || 'connecting';
    const externalTargetId = draft.selectedTargetId || null;
    const targetId = externalTargetId
      ? `${p}:${externalTargetId}`
      : (bs?.selectedByPlatform?.[p]?.targetId || null);

    return {
      status,
      accountId: draft.accountId || null,
      targetId,
      adsId: draft.selectedAdsId || null,
      externalTargetId,
      expiresAt: acc?.expiresAt || null
    };
  }

  // 2) COMMITTED (from BrandSettings.selectedByPlatform)
  const bs  = selectBrandSettingsById(bsId)(state) || null;
  const sel = bs?.selectedByPlatform?.[p] || null;
  if (sel) {
    const accId = sel.accountId ? String(sel.accountId) : null;

    // Prefer accounts scoped to *this* BrandSettings to avoid cross-brand leakage
    const acc =
      (bs?.socialAccounts || []).find(a => String(a?._id) === accId) ||
      // fallback to your global buckets if you keep them
      get(state, `social.accounts.byId.${accId}`) ||
      get(state, `user.socialAccounts.byId.${accId}`) ||
      get(state, `user.accounts.byId.${accId}`) ||
      null;

    return {
      status: deriveStatusFromAccount(acc),
      accountId: accId,
      targetId: sel.targetId || null,
      adsId: sel.adsId || null,
      externalTargetId: sel.externalTargetId || null,
      expiresAt: acc?.expiresAt || null
    };
  }

  // 3) SESSION-LEVEL (social.connections[platform])
  const conn = state?.social?.connections?.[p] || null;
  if (conn) {
    const externalTargetId = conn.selectedTargetId || null;
    const targetId = externalTargetId ? `${p}:${externalTargetId}` : null;

    return {
      status: conn.status || 'connected',
      accountId: conn.accountId || null,
      targetId,
      adsId: conn.selectedAdsId || null,
      externalTargetId,
      expiresAt: null
    };
  }

  return null;
};

/**
 * Convenience selector: return targets for an account and ensure each has targetStatus.
 * If targets already carry targetStatus, they’re left as-is.
 * Otherwise we inject the platform-level status from BrandSettings.
 */
export const selectTargetsWithStatus = (accountId, brandSettingsId, platform) => (state) => {
  const targets = selectTargetsWithStatusByAccount(accountId)(state) || [];
  const conn = selectPlatformConnectionForBrandSettings(brandSettingsId, platform)(state);
  const fallbackStatus = conn?.status || 'disconnected';

  return targets.map(t => ({
    ...t,
    targetStatus: t.targetStatus || fallbackStatus
  }));
};

